import { CommonModule } from '@angular/common'
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { v4 as uuid } from 'uuid'

import {
  Observable,
  Subject,
  Subscription,
  catchError,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs'

import { FlxButtonModule, FlxModalService } from '@flowx/angular-ui-toolkit'
import { resetInputFileValueFromEvent } from '@flowx/designer/modules/ai/common/ai.utils'
import {
  ConversationDocumentAttentionEntryInput,
  ConversationDocumentAttentionEntryType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AgentName } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { ConversationDocument } from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import {
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { AiExecutionPlanService } from '@flowx/designer/modules/ai/conversation/ai-execution-plan.service'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import {
  AiDocumentContentType,
  AiDocumentContentTypeLabelMap,
  AiDocumentStatus,
  AiDocumentSummary,
} from '@flowx/designer/modules/ai/types/ai.types'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { selectProcessResourceId } from '@flowx/designer/shared/store/selectors/router.selectors'

import {
  ContextDocumentContent,
  ContextDocumentResponse,
} from '../../../../agents/documents/api/documents.api'
import { AiDocumentsApi } from '../../../../agents/documents/api/documents.api'
import { typeToAgentMap } from '../../../../agents/documents/services/ai-documents-transform.service'
import { AiDocumentsService } from '../../../../agents/documents/services/ai-documents.service'
import { ConversationActions } from '../../../../conversation/conversation.actions'
import { AiLoadingSpinnerComponent } from '../../../external/components/ai-loading-spinner/ai-loading-spinner.component'
import { DocumentResourceListComponent } from '../document-resource-list/document-resource-list.component'

const AgentNameToDocumentTypeMap: Partial<Record<AgentName, AiDocumentContentType>> = {
  [AgentName.AiDeveloper]: AiDocumentContentType.BUSINESS_RULE,
  [AgentName.AiDesigner]: AiDocumentContentType.SCREEN_NAME,
  [AgentName.AiAnalyst]: AiDocumentContentType.BUSINESS_PROCESS,
}

// KB ingestion (system KB) has no per-document SSE here yet — poll while any doc is processing.
const PROCESSING_POLL_INTERVAL_MS = 5000

@Component({
  selector: 'flx-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss'],
  imports: [
    CommonModule,
    FlxButtonModule,
    DocumentResourceListComponent,
    AiLoadingSpinnerComponent,
  ],
})
export class DocumentsListComponent implements OnInit {
  #aiDocumentsApi = inject(AiDocumentsApi)
  #aiConversationService = inject(AiConversationService)
  #aiExecutionPlanService = inject(AiExecutionPlanService)
  #store = inject(Store)
  #documentsOperationsService = inject(AiDocumentsService)
  #destroyRef = inject(DestroyRef)
  #flxModalService = inject(FlxModalService)
  #actions = inject(Actions)
  #toastService = inject(ToastMessageService)
  #refresh = new Subject<void>()
  #pollSubscription: Subscription | null = null

  documents = signal<(ConversationDocument & { isNew?: boolean })[]>([])
  failedDocuments = computed(() =>
    this.documents().filter((doc) => doc.status === AiDocumentStatus.FAILED)
  )
  AiDocumentStatus = AiDocumentStatus
  selectedDocument = signal<ConversationDocument | null>(null)
  allowedDocumentFileTypes = this.#store.selectSignal(
    ConversationSelectors.allowedDocumentFileTypes
  )
  resourceType = signal<AiDocumentContentType | null>(null)
  useInCurrentThread = signal(false)
  showResourceList = this.#store.selectSignal(ConversationSelectors.showDocumentResources)

  selectedProcessResourceId = this.#store.select(selectProcessResourceId)
  latestMessage = this.#store.selectSignal(ConversationSelectors.latestMessageFromAgent)
  agent = computed(() =>
    JSON.parse(
      this.latestMessage()?.contextData.items.find((item) => item.key === ContextDataKeys.UiAgent)
        ?.valueGenericString || '{}'
    )
  )
  agentName = computed(() => this.agent()?.agent)
  agentDocumentType = computed(
    () => AgentNameToDocumentTypeMap[this.agentName() || AgentName.AiAnalyst]
  )
  hasExecutionPlan = this.#store.selectSignal(ConversationSelectors.currentExecutionPlanExists)
  processingDocumentsNames = computed(() =>
    this.documents()
      .filter((doc) => !doc.isReady && doc.status !== AiDocumentStatus.FAILED)
      .map((doc) => doc.filename)
  )
  activeAgentTarget = this.#store.selectSignal(ConversationSelectors.activeAgentTarget)
  hasPendingOutcome = this.#store.selectSignal(
    ConversationSelectors.activeAgentMainOutcomeFinished
  )
  uploadedDocumentIds = this.#store.selectSignal(ConversationSelectors.uploadedDocumentIds)

  ngOnInit(): void {
    this.#refresh
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        switchMap(() =>
          this.resourceType()
            ? this.#aiDocumentsApi.getDocuments(typeToAgentMap[this.resourceType()])
            : this.#aiDocumentsApi.getDocumentsByAppId()
        ),
        map((documents) =>
          documents
            .map((document) => this.#mapDocument(document))
            .filter((doc) => doc !== null)
            .sort((a, b) => new Date(a.modifiedDate).getTime() - new Date(b.modifiedDate).getTime())
        ),
        map((documents) =>
          documents.filter(
            (doc) =>
              !doc.isReady ||
              doc.content.some((content) =>
                Object.values(AgentNameToDocumentTypeMap).includes(content.type)
              )
          )
        ),
        tap((documents) => {
          documents.forEach((doc) => {
            const document = this.documents().find((d) => d.id === doc.id)
            if (document && document.isNew) {
              doc.isNew = true
            }

            if (document && document.status !== doc.status) {
              if (
                [AiDocumentStatus.READY, AiDocumentStatus.EVALUATED].includes(
                  doc.status.toLowerCase() as AiDocumentStatus
                ) &&
                this.uploadedDocumentIds().includes(document.id)
              ) {
                doc.isNew = true

                this.#toastService.showSuccessMessage(`${document.filename} is ready to use`, null, {
                  appendTo: 'body',
                })

                this.#store.dispatch(ConversationActions.documentReady({ documentId: document.id }))
              }

              if (
                doc.status.toLowerCase() === AiDocumentStatus.FAILED &&
                this.uploadedDocumentIds().includes(document.id)
              ) {
                this.#toastService.showErrorMessage("The file couldn't be processed.", null, {
                  appendTo: 'body',
                })
              }
            }
          })
          this.documents.set(documents)
          this.#schedulePollWhileProcessing(documents)
        })
      )
      .subscribe()

    this.#actions
      .pipe(ofType(ConversationActions.documentContextClosed), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.documents.set(this.documents().map((doc) => ({ ...doc, isNew: false })))
      })

    this.#actions
      .pipe(ofType(ConversationActions.documentContextOpened), takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ resourceType, useInCurrentThread }) => {
        this.resourceType.set(resourceType)
        this.useInCurrentThread.set(useInCurrentThread ?? false)
        this.#refresh.next()
      })

    this.selectedProcessResourceId.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((id) => {
      if (!id) {
        this.#store.dispatch(ConversationActions.documentSelected({ document: null }))
        this.#store.dispatch(ConversationActions.documentContextClosed())
      }
    })
  }

  onCancelProcessing(document: ConversationDocument, event: Event): void {
    event.stopPropagation()
    this.#documentsOperationsService.deleteDocument(document).subscribe(() => {
      this.#refresh.next()
    })
  }

  onRetryDocument(document: ConversationDocument, event: Event): void {
    event.stopPropagation()
    this.documents.set(
      this.documents().map((d) =>
        d.id === document.id ? { ...d, status: AiDocumentStatus.PROCESSING } : d
      )
    )
    this.#documentsOperationsService
      .retryDocumentExtraction(document as any)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#refresh.next()
        },
        error: () => {
          this.documents.set(
            this.documents().map((d) =>
              d.id === document.id ? { ...d, status: AiDocumentStatus.FAILED } : d
            )
          )
          this.#toastService.showErrorMessage('Retry failed. Please try again.', null, {
            appendTo: 'body',
          })
        },
      })
  }

  onDocumentClick(document: ConversationDocument): void {
    if (!document.isReady) {
      return
    }

    const hasMultiple =
      document.content.filter((c) => c.type === AiDocumentContentType.BUSINESS_PROCESS).length > 1
    if (hasMultiple) {
      this.#store.dispatch(ConversationActions.documentResourcesOpened())
      this.selectedDocument.set(document)

      return
    }

    this.startFromDocument(document)
  }

  onCloseClick(): void {
    this.#store.dispatch(ConversationActions.documentContextClosed())
  }

  startFromDocument(document: ConversationDocument, resource?: ContextDocumentContent): void {
    const attentionEntry = this.#getAttentionEntry(resource)

    if (this.useInCurrentThread()) {
      this.#store.dispatch(ConversationActions.documentContextClosed())
      this.#aiConversationService
        .submitMessage(
          {
            prompt: `Use ${resource ? resource.name + ' from' : ''} ${document.filename}`,
            type: MessageType.UserMsg,
          },
          [
            {
              docID: document.diDocumentId,
              attention: {
                entries: attentionEntry ? [attentionEntry] : [],
              },
            },
          ]
        )
        .subscribe()

      return
    }

    const startConversation = (): Observable<unknown> => {
      this.#store.dispatch(ConversationActions.documentSelected({ document }))

      return this.#aiConversationService.startConversationWithDocument(document, resource, [
        attentionEntry,
      ])
    }

    if (!this.activeAgentTarget() && !this.hasExecutionPlan()) {
      startConversation().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()

      return
    }

    const closeThenStartConversation = (): Observable<unknown> =>
      this.#aiExecutionPlanService
        .deleteExecutionPlan()
        .pipe(switchMap(() => startConversation()))

    if (!this.hasExecutionPlan() && !this.hasPendingOutcome()) {
      closeThenStartConversation().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()

      return
    }

    const title = this.hasExecutionPlan()
      ? 'The existing execution plan will be lost if you switch contexts. Are you sure?'
      : 'Changing context will discard the generated content that was not yet saved and generate a new plan. Are you sure you want to change context?'

    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        {
          closeOnBackdropClick: false,
          backdropClass: 'overlay-backdrop',
          appendTo: 'body',
        },
        {
          title,
          btnOkText: 'Yes',
          btnCancelText: 'No',
          btnOkType: 'danger',
        }
      )
      .data$.pipe(
        filter((data) => data.confirm),
        take(1),
        switchMap(() => closeThenStartConversation()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  async onDocumentSelected(event: Event): Promise<void> {
    const files = Array.from((event.target as HTMLInputElement).files).map((file) => ({
      file,
      name: file.name,
      type: file.type,
      id: uuid(),
    }))

    if (files.length > 1) {
      resetInputFileValueFromEvent(event)
      throw new Error('Multiple files are not supported')
    }

    this.#documentsOperationsService
      .uploadDocument(files[0])
      .pipe(
        catchError(() => {
          this.#toastService.showErrorMessage("The file couldn't be uploaded.", null, {
            appendTo: 'body',
          })
          resetInputFileValueFromEvent(event)
          return of(null)
        }),
        tap((document: AiDocumentSummary | null) => {
          if (document) {
            resetInputFileValueFromEvent(event)
            this.#store.dispatch(ConversationActions.documentUploaded({ documentId: document.id }))
            this.#refresh.next()
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  onResourceListClosed(
    event: { document: ConversationDocument; resource: ContextDocumentContent } | null
  ): void {
    this.#store.dispatch(ConversationActions.documentResourcesClosed())

    if (event) {
      this.startFromDocument(event.document, event.resource)
    }
  }

  hasContentType = (document: ContextDocumentResponse, type: AiDocumentContentType): boolean =>
    !!document.details?.content.find((c) => c.type === type)

  documentIsReady = (document: ContextDocumentResponse | ConversationDocument): boolean =>
    document.status.toLowerCase() === AiDocumentStatus.READY ||
    document.status.toLowerCase() === AiDocumentStatus.EVALUATED

  #mapDocument(
    contextDocument: ContextDocumentResponse
  ): (ConversationDocument & { isNew?: boolean }) | null {
    if (
      this.resourceType() &&
      !this.hasContentType(contextDocument, this.resourceType()) &&
      this.documentIsReady(contextDocument)
    ) {
      return null
    }

    return {
      id: contextDocument.id,
      diDocumentId: contextDocument.id,
      cmsDocument: null,
      filename: contextDocument.file_name,
      content: contextDocument.details?.content || [],
      status: contextDocument.status || AiDocumentStatus.PROCESSING,
      modifiedDate: contextDocument.created_at,
      tags: [
        this.hasContentType(contextDocument, AiDocumentContentType.BUSINESS_PROCESS)
          ? AiDocumentContentTypeLabelMap[AiDocumentContentType.BUSINESS_PROCESS]
          : null,
        this.hasContentType(contextDocument, AiDocumentContentType.SCREEN_NAME)
          ? AiDocumentContentTypeLabelMap[AiDocumentContentType.SCREEN_NAME]
          : null,
        this.hasContentType(contextDocument, AiDocumentContentType.BUSINESS_RULE)
          ? AiDocumentContentTypeLabelMap[AiDocumentContentType.BUSINESS_RULE]
          : null,
      ].filter(Boolean),
      isReady: this.documentIsReady(contextDocument),
    }
  }

  #schedulePollWhileProcessing(documents: (ConversationDocument & { isNew?: boolean })[]): void {
    this.#pollSubscription?.unsubscribe()

    const hasProcessingDocuments = documents.some(
      (doc) => !doc.isReady && doc.status.toLowerCase() !== AiDocumentStatus.FAILED
    )
    if (hasProcessingDocuments) {
      this.#pollSubscription = timer(PROCESSING_POLL_INTERVAL_MS)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#refresh.next())
    }
  }

  #getAttentionEntry(resource?: ContextDocumentContent): ConversationDocumentAttentionEntryInput {
    if (resource?.type === AiDocumentContentType.BUSINESS_PROCESS) {
      return {
        type: ConversationDocumentAttentionEntryType.Process,
        process: { name: resource?.name },
      }
    }

    if (resource?.type === AiDocumentContentType.SCREEN_NAME) {
      return {
        type: ConversationDocumentAttentionEntryType.ScreenName,
        screenName: { name: resource?.name },
      }
    }

    if (resource?.type === AiDocumentContentType.BUSINESS_RULE) {
      return {
        type: ConversationDocumentAttentionEntryType.BusinessRule,
        businessRule: { name: resource?.name },
      }
    }

    return {
      type: ConversationDocumentAttentionEntryType.Unknown,
    }
  }
}
